import router from '@/router';
import api from '@/services/Api';
import store from '@/store';
import qs from 'qs';
import { toast } from 'vue3-toastify';
import { useRoute } from 'vue-router';
import axios from 'axios';

export default class Model {
    static cancelTokens = {};

    constructor (properties) {
        properties = properties || {};
        this.assign(properties, true);

        this.api = api;
        this.filters = null;
    }

    static setFilters (filters) {
        this.filters = filters;
    }

    /**
     * @param {object} properties
     * @param {boolean} [original] Pass true to save the properties as original, refreshing dirty state.
     */
    assign (properties, original) {
        if (original) {
            this.originalProperties = properties;
        }
        Object.assign(this, properties);
    }

    isDirty (property) {
        return this.originalProperties[property] !== this[property];
    }

    /**
     * @return {Array}
     */
    getDirty () {
        const arr = {};
        for (const prop in this.originalProperties) {
            if (this.isDirty(prop)) {
                arr[prop] = this[prop];
            }
        }
        return arr;
    }

    /**
     * @param {object} query
     * @return {string}
     */
    static makeQuery (query) {
        return qs.stringify(query || {}, { encodeValuesOnly: true });
    }

    /**
     * @param {Object} [query]
     * @param {String} [url] Append a piece to the current route, for example pass '/ids' to request '/categories/ids'.
     * @param {String} [cancelToken] Pass a (unique) identifier so calls with the same id will cancel the previous
     * request.
     * @return {Promise}
     */
    static index (query, url = '', cancelToken) {
        if (cancelToken) {
            if (this.cancelTokens[cancelToken]) {
                this.cancelTokens[cancelToken].cancel();
                delete this.cancelTokens[cancelToken];
            }
            this.cancelTokens[cancelToken] = axios.CancelToken.source();
        }

        query = this.makeQuery(query);
        query = query ? '?' + query : query;
        const urlBuilder = this.route + url + query;
        return api
            .get(urlBuilder, { cancelToken: (this.cancelTokens[cancelToken] || {}).token })
            .then((response) => {
                if (cancelToken) {
                    delete this.cancelTokens[cancelToken];
                }

                if (query.includes('paginate')) {
                    response.data.data = this.map(response.data.data);
                } else {
                    response.data = this.map(response.data);
                }
                return response;
            }).catch((error) => {
                if (axios.isCancel(error)) {
                    // Handle cancellation
                    return error.message;
                } else {
                    return error;
                }
            });
    }

    /**
     * Search this model with a given keyword.
     * @param {string} search
     * @param {Array} [builder] Optional: pass items to prepend to the builder object.
     * @return {Promise}
     */
    static search (search, builder = []) {
        return this.index({
            builder: [{ s: search }, ...builder],
        });
    }

    static get isOrdered () {
        return false;
    }

    static get isInStore () {
        return false;
    }

    /*
     * Needed because files are getting uglyfied, so constructor.name does not work in production
     * Needs to be overridden in Model subclasses if Vuex is used for that type of model
     */
    static get className () {
        throw new Error('NotDefinedInSubclass');
    }

    get route () {
        throw new Error('NotDefinedInSubclass');
    }

    static get route () {
        return new this().route;
    }

    /*
     * Is overridden in some Model subclasses
     */
    static get enumKey () {
        return this.route;
    }

    static get storageKey () {
        return this.filters?.toCamelCase(this.route);
    }

    static get commitSet () {
        return `SET_${this.filters?.toCamelCase(this.route).toUpperCase()}`;
    }

    static get commitUpdate () {
        return `UPDATE_${this.filters?.toSnakeCase(this.className).toUpperCase()}`;
    }

    static get commitRemove () {
        return `REMOVE_${this.filters?.toSnakeCase(this.className).toUpperCase()}`;
    }

    get primaryKey () {
        return 'id';
    }

    static get primaryKey () {
        return new this().primaryKey || 'id';
    }

    get storePrimaryKey () {
        return 'id';
    }

    static get storePrimaryKey () {
        return new this().storePrimaryKey || 'id';
    }

    static map (data) {
        if (data.map) {
            return data.map((item) => {
                return new this(item);
            });
        }
        return data;
    }

    static find (key, query) {
        query = query || {};
        return api.get(`${this.route}/${key}?${qs.stringify(query)}`).then((response) => {
            response.data = new this(response.data);
            return response;
        }).catch((e) => {
            if (e.response && e.response.status === 404) {
                if (!useRoute()?.name) {
                    router.push({ name: '404' });
                    return Promise.reject(e);
                }

                const currentRoute = useRoute().name.split('.');
                if (this.route === currentRoute[0] && currentRoute[1] === 'show') {
                    router.push({ name: `${currentRoute[0]}.index` });
                    return Promise.reject(e);
                }
            }
            throw e;
        });
    }

    static create (properties) {
        return api.post(this.route, properties).then((response) => {
            const data = new this(response.data);
            if (this.isInStore) {
                store.commit(this.commitUpdate, data);
            }
            return data;
        });
    }

    getCustomValueLabel (key) {
        if (!this.custom_values) {
            return '';
        }

        const filtered = this.custom_values.filter((item) => {
            return item.custom_field === key;
        });

        if (filtered.length) {
            return filtered[0]._label;
        }

        return '';
    }

    update (data, confirmMe, toastr = false) {
        if (!confirmMe || confirm(confirmMe)) {
            let route = this.route;
            if (this.is_template && this.route === 'tickets') {
                route = 'ticket-templates';
            }
            return api.put(`${route}/${this[this.primaryKey]}`, data).then((response) => {
                this.assign(response.data, true);
                if (this.constructor.isInStore) {
                    store.commit(this.constructor.commitUpdate, this);
                }
                if (toastr) {
                    toast('Item bijgewerkt.', {
                        type: toast.TYPE.SUCCESS,
                    });
                }
                response.data = this;
                return response;
            });
        }
        return Promise.reject(new Error('Not confirmed.'));
    }

    destroy (confirmMe = 'Echt verwijderen?', toastr = true) {
        if (!confirmMe || confirm(confirmMe)) {
            return api.delete(`${this.route}/${this[this.primaryKey]}`).then(() => {
                if (this.constructor.isInStore) {
                    store.commit(this.constructor.commitRemove, this[this.storePrimaryKey]);
                }
                if (toastr) {
                    toast('Item verwijderd.', {
                        type: toast.TYPE.SUCCESS,
                    });

                    const currentRoute = useRoute().name.split('.');
                    if (this.route === currentRoute[0] && currentRoute[1] === 'show') {
                        router.push({ name: `${currentRoute[0]}.index` });
                    }
                }
            });
        }

        return Promise.reject(new Error('Not confirmed.'));
    }

    static getDefault () {
        const def = store.getters.getFilteredValue(this.storageKey, item => item.is_default);
        return def ? def.id : null;
    }

    static invoice (ids, confirmMe = 'Echt factureren?', toastr = true) {
        if (!confirmMe || confirm(confirmMe)) {
            return api.post(`${this.route}/invoice`, { ids }).then((response) => {
                if (toastr && response.data) {
                    toast('Regel toegevoegd aan conceptfactuur.', {
                        type: toast.TYPE.SUCCESS,
                    });
                }
            });
        }

        return Promise.reject(new Error('Not confirmed.'));
    }

    static deinvoice (ids, confirmMe = 'Echt van factuur halen?', toastr = true) {
        if (!confirmMe || confirm(confirmMe)) {
            return api.post(`${this.route}/deinvoice`, { ids }).then(() => {
                if (toastr) {
                    toast('Regel verwijderd van conceptfactuur.', {
                        type: toast.TYPE.SUCCESS,
                    });
                }
            });
        }

        return Promise.reject(new Error('Not confirmed.'));
    }

    static getPresetsResultCount (builders) {
        return api.get(`/presets-result-count/${this.route}?${this.makeQuery({ builders })}`);
    }
}
